import { parseTagFacets } from "../utils/tag-facet-param";
import { googleEvent } from "../utils/dom";
import { useEffect, useState } from "react";
import { useProduct, useUI, useRouterContext } from "../contexts";
import { useSearch, SearchFilters, isValidSearch } from "@/contexts/SearchProvider";
import Img from "./img";
import shared from "../data/js/shared";
import { CONDITIONS, Cats, getCategoryById } from "../generated/static-categories";
import { ATTRIBUTE_FILTER_KEYS, activeFilterCount, filtersFromQuery, filtersToQuery, type FilterState } from "../utils/search-filter-state";

const { placeholderLabel } = shared.top;

/** How a saved search's attribute filters read on its chips. */
const ATTRIBUTE_CHIP_LABELS: Record<(typeof ATTRIBUTE_FILTER_KEYS)[number], string> = {
	gender: "FOR", ageGroup: "AGE", size: "SIZE", color: "COLOR", brand: "BRAND", material: "MATERIAL",
};
const VALUE_LABELS: Record<string, string> = { female: "Women", male: "Men", unisex: "Unisex", adult: "Adult", kids: "Kids", baby: "Baby" };

/** A saved search's tag and type filters as one chip's text: "Dogs, Puppies, Adoption". Empty when none. */
export function tagFacetChipText(tf: string | undefined): string {
	const facets = parseTagFacets(tf) ?? {};
	return Object.entries(facets)
		.flatMap(([key, values]) => values.map(v => (key === "type" ? getCategoryById(Number(v))?.label ?? v : v.replace(/\b\w/g, c => c.toUpperCase()))))
		.join(", ");
}

/**
 * The /search URL for a query, a category group and a filter set. Only called
 * for a search that has one of the three (a typed query, or a recent search
 * that passed isValidSearch), so the query string is never empty.
 */
export function searchPath(q: string, categoryGroupId: number | undefined, filters: FilterState): string {
	const params = new URLSearchParams({
		...(q ? { q } : {}),
		...filtersToQuery({ ...filters, categoryGroupId }, { withCategoryGroup: true }),
	});
	return `/search?${params.toString()}`;
}

const SearchBar = () => {
	const { removeProduct } = useProduct();
	const { setSearchParams, setRecentSearch, searchParams, recentSearches, searchHistory, currentCategoryGroupId, setCurrentCategoryGroupId } = useSearch();
	const { setSearchFocus, setFilterDrawerOpen } = useUI();
	const [query, setQuery] = useState({ ...searchParams });
	const { push } = useRouterContext();
	const [selectedCategory, setSelectedCategory] = useState(currentCategoryGroupId || 0);


	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- syncs external SearchProvider context (searchParams) into local query state that is also independently edited by the user; genuine external-state synchronization.
		setQuery(searchParams);
	}, [searchParams]);

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- syncs external SearchProvider context (currentCategoryGroupId) into local selectedCategory that is also independently edited by the user; genuine external-state synchronization.
		setSelectedCategory(currentCategoryGroupId || 0);
	}, [currentCategoryGroupId]);

	/** Go to /search for this query, category and filters, and remember it. */
	const runSearch = (q: string, categoryGroupId: number | undefined, filters: FilterState) => {
		// A real group only. The category bar marks For You with -1, and it rode
		// into the search URL from here, emptying the results.
		const group = categoryGroupId && categoryGroupId > 0 ? categoryGroupId : undefined;
		const params: SearchFilters = { ...filters, q, page: 1, ...(group ? { categoryGroupId: group } : {}) };
		removeProduct();
		setSearchParams(params);
		setRecentSearch(params);
		setSearchFocus(false);
		if (q) googleEvent("search", { search_term: q });
		if (document.activeElement) {
			(document.activeElement as HTMLElement).blur();
		}
		push(searchPath(q, group, filters));
	};

	const searchSubmitted = async (query: SearchFilters) => {
		if (!query?.q || query.q.trim().length == 0) {
			push("/");
			return;
		}
		// A new query keeps the filters already applied.
		const filters = filtersFromQuery({ ...searchParams, ...query } as Record<string, never>);
		runSearch(query.q, selectedCategory, filters);
	};

	const clickRecentSearch = (rs: SearchFilters) => {
		if (rs.categoryGroupId !== undefined) {
			setSelectedCategory(rs.categoryGroupId);
			setCurrentCategoryGroupId(rs.categoryGroupId);
		}
		const filters = filtersFromQuery(rs as Record<string, never>);
		setQuery({ ...filters, q: rs.q || "" });
		runSearch(rs.q || "", rs.categoryGroupId, filters);
	};

	// One per filter set, not per value: four conditions ticked is one filter.
	const filterCount = activeFilterCount(filtersFromQuery(query as Record<string, never>));
	const filtersActive = filterCount > 0;

	return (
		<>
			<form
				className="search-bar search-form mx-2!"
				method="GET"
				action="/search"
				data-testid="search-form"
				onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
					e.preventDefault();
					void searchSubmitted(query);
				}}
			>
				<Img src="/images/svg/search.svg" className="search-icon" data-testid="search-icon" />
				{query.q?.length ? (
					<Img
						src="/images/svg/close-x.svg"
						className="close-icon"
						data-testid="search-clear-btn"
						onClick={() => {
							// Clear the query and every filter: setSearchParams merges, and an
							// undefined key is the only way to take a filter out.
							setQuery({ q: "" });
							setSearchParams({
								q: "", categoryGroupId: 0, page: 1,
								minPrice: undefined, maxPrice: undefined, itemCondition: undefined, sold: undefined, shippable: undefined, featured: undefined, deals: undefined, quality: undefined,
								...Object.fromEntries(ATTRIBUTE_FILTER_KEYS.map(k => [k, undefined])),
								tf: undefined,
							});
							setSearchFocus(false);
							push("/");
						}}
					/>
				) : (
					""
				)}
				<input
					className="search-input"
					inputMode="search"
					autoComplete={"off"}
					name="q"
					placeholder={placeholderLabel}
					type="search"
					data-testid="search-input"
					value={query.q || ""}
					onFocus={() => {
						setSearchFocus(true);
					}}
					onBlur={() => {
						setTimeout(() => {
							setSearchFocus(false);
						}, 200);
					}}
					onInput={(e) => {
						const q = (e.target as HTMLInputElement).value;
						setQuery({ ...query, q });
					}}
				/>

				<input
					type="submit"
					className="search-button same-color-2"
					value="Search"
					data-testid="search-submit-btn"
				/>

				{/* Filter button: opens the filter drawer (components/filter-drawer.tsx). */}
				<button
					type="button"
					className={`filter-button-text ${filtersActive ? 'has-filters' : ''}`}
					onClick={() => setFilterDrawerOpen(true)}
					title="Search Filters"
					data-testid="search-filter-btn"
				>
					{filterCount > 1 ? 'Filters' : 'Filter'}
					{/* The count, in a corner circle like the notification bell's
					    (`.filter-badge-dot` extends `.countCircle`). */}
					{filtersActive && (
						<span className="filter-badge-dot" data-testid="search-filter-dot" aria-label={`${filterCount} active`}>
							{filterCount}
						</span>
					)}
				</button>

				<div className="recentSearches recent-searches-dropdown bg-white border border-gray-200 shadow-lg dark:bg-[#1a1a1a] dark:border-neutral-700">
					<div className="overflow-y-auto recent-searches-inner text-gray-900 dark:text-gray-100">
						{/* Combined Recent Searches - Using recentSearches as primary, searchHistory as fallback */}
						{((recentSearches && recentSearches.length > 0) || (searchHistory && searchHistory.length > 0)) && (
							<>
								<div className="px-4 sm:px-5 py-3 font-semibold text-gray-500 uppercase tracking-wider bg-white border-b border-gray-200 sticky top-0 recent-searches-header dark:text-gray-400 dark:bg-[#1a1a1a] dark:border-neutral-700">
									Recent Searches
								</div>
								{(recentSearches && recentSearches.length > 0 ? recentSearches : searchHistory)
									.filter(isValidSearch)
									.slice(0, 10)
									.map((r, _i) => {
											const conditions: Record<number, string> = {};
											CONDITIONS.forEach(c => {
											  conditions[c.id] = c.label;
											});
											// Handle itemCondition as either single number or array
											const itemConditions = Array.isArray(r.itemCondition) ? r.itemCondition : (r.itemCondition ? [r.itemCondition] : []);

											return (
												<div
													className="px-4 py-3 hover:bg-gray-100 cursor-pointer transition-colors border-b border-gray-200 last:border-b-0 min-h-[64px] dark:hover:bg-neutral-800 dark:border-neutral-700 flex items-center"
													onClick={() => clickRecentSearch(r)}
													key={`search-${JSON.stringify(filtersToQuery({ ...filtersFromQuery(r as Record<string, never>) }, { withCategoryGroup: true }))}-${r.q || ''}`}
												>
													<div className="flex items-center justify-between w-full">
														<div className="flex-1">
															<div className="flex flex-wrap items-center gap-2.5">
																{r.q && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-purple-50 text-purple-800 border border-purple-300 dark:bg-purple-900/40 dark:text-purple-100 dark:border-purple-700/50 max-w-full">
																		<span className="hidden sm:inline text-purple-600 mr-1.5 recent-search-label dark:text-purple-300">SEARCH:</span>
																		<span className="truncate">{r.q}</span>
																	</span>
																)}
																{r.categoryGroupId && r.categoryGroupId !== 0 && Cats[r.categoryGroupId] && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-orange-50 text-orange-800 border border-orange-300 dark:bg-orange-900/40 dark:text-orange-100 dark:border-orange-700/50 whitespace-nowrap">
																		<span className="hidden sm:inline text-orange-600 mr-1.5 recent-search-label dark:text-orange-300">CATEGORY:</span>
																		{Cats[r.categoryGroupId].label}
																	</span>
																)}
																{r.minPrice && r.maxPrice && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-800 border border-blue-300 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-700/50 whitespace-nowrap">
																		<span className="hidden sm:inline text-blue-600 mr-1.5 recent-search-label dark:text-blue-300">PRICE:</span>
																		${r.minPrice}-${r.maxPrice}
																	</span>
																)}
																{r.minPrice && !r.maxPrice && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-800 border border-blue-300 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-700/50 whitespace-nowrap">
																		<span className="hidden sm:inline text-blue-600 mr-1.5 recent-search-label dark:text-blue-300">MIN:</span>
																		${r.minPrice}+
																	</span>
																)}
																{!r.minPrice && r.maxPrice && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-800 border border-blue-300 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-700/50 whitespace-nowrap">
																		<span className="hidden sm:inline text-blue-600 mr-1.5 recent-search-label dark:text-blue-300">MAX:</span>
																		&lt;${r.maxPrice}
																	</span>
																)}
																{itemConditions.map((condId, idx) => (
																	conditions[condId] && (
																		<span key={`cond-${condId}`} className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-green-50 text-green-800 border border-green-300 dark:bg-green-900/40 dark:text-green-100 dark:border-green-700/50 whitespace-nowrap">
																			{idx === 0 && <span className="hidden sm:inline text-green-600 mr-1.5 recent-search-label dark:text-green-300">CONDITION:</span>}
																			{conditions[condId]}
																		</span>
																	)
																))}
																{r.sold && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-red-50 text-red-800 border border-red-300 dark:bg-red-900/40 dark:text-red-100 dark:border-red-700/50 whitespace-nowrap">
																		<span className="text-red-600 recent-search-label dark:text-red-300">SOLD</span>
																	</span>
																)}
																{r.shippable && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-teal-50 text-teal-800 border border-teal-300 dark:bg-teal-900/40 dark:text-teal-100 dark:border-teal-700/50 whitespace-nowrap">
																		<span className="text-teal-600 recent-search-label dark:text-teal-300">SHIPPABLE</span>
																	</span>
																)}
																{r.deals && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-green-50 text-green-800 border border-green-300 dark:bg-green-900/40 dark:text-green-100 dark:border-green-700/50 whitespace-nowrap">
																		<span className="text-green-600 recent-search-label dark:text-green-300">DEALS</span>
																	</span>
																)}
																{r.quality && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-indigo-50 text-indigo-800 border border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-100 dark:border-indigo-700/50 whitespace-nowrap">
																		<span className="text-indigo-600 recent-search-label dark:text-indigo-300">QUALITY</span>
																	</span>
																)}
																{r.featured && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-yellow-50 text-yellow-800 border border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-100 dark:border-yellow-700/50 whitespace-nowrap">
																		<span className="text-yellow-600 recent-search-label dark:text-yellow-300">FEATURED</span>
																	</span>
																)}
																{ATTRIBUTE_FILTER_KEYS.map(key => {
																	const values = r[key];
																	if (!Array.isArray(values) || values.length === 0) return null;
																	return (
																		<span key={key} className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-gray-100 text-gray-800 border border-gray-300 dark:bg-neutral-800 dark:text-gray-100 dark:border-neutral-600 max-w-full" data-testid={`recent-chip-${key}`}>
																			<span className="hidden sm:inline text-gray-500 mr-1.5 recent-search-label dark:text-gray-400">{ATTRIBUTE_CHIP_LABELS[key]}:</span>
																			<span className="truncate">{values.map(v => VALUE_LABELS[v] ?? v).join(", ")}</span>
																		</span>
																	);
																})}
																{tagFacetChipText(r.tf) && (
																	<span className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium bg-gray-100 text-gray-800 border border-gray-300 dark:bg-neutral-800 dark:text-gray-100 dark:border-neutral-600 max-w-full" data-testid="recent-chip-tf">
																		<span className="truncate">{tagFacetChipText(r.tf)}</span>
																	</span>
																)}
															</div>
														</div>
														<svg className="w-5 h-5 text-gray-400 ml-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
														</svg>
													</div>
												</div>
											);
										})}
									</>
								)}

						{/* Show message if no searches */}
						{(!searchHistory || searchHistory.length === 0) && (!recentSearches || recentSearches.length === 0) && (
							<div className="px-4 py-8 text-center">
								<div className="text-gray-400">No recent searches</div>
								<div className="text-gray-400 mt-1 recent-search-label">Your search history will appear here</div>
							</div>
						)}
					</div>
				</div>
				<div
					className="recentSearches-bg"
					onClick={() => setSearchFocus(false)}
				/>
			</form>

		</>
	);
};

export default SearchBar;
